import { getDocument } from '@atlaskit/browser-apis';

type StreamTimeoutPauseController = {
	isPaused: () => boolean;
	dispose: () => void;
};

/**
 * Prevents background-tab throttling from consuming the stream idle budget.
 * A full timeout window is granted after the document becomes visible again,
 * so a delayed background timer cannot fail the stream immediately on resume.
 */
export const createStreamTimeoutPauseController = (
	timeoutMs: number,
): StreamTimeoutPauseController => {
	let resumeGraceUntil = 0;
	const browserDocument = getDocument();
	const browserWindow = browserDocument?.defaultView;

	const handleVisibilityChange = () => {
		if (browserDocument?.visibilityState === 'visible') {
			resumeGraceUntil = Date.now() + timeoutMs;
		}
	};

	browserDocument?.addEventListener('visibilitychange', handleVisibilityChange);
	browserWindow?.addEventListener('online', handleVisibilityChange);

	return {
		isPaused: () => {
			const isOffline = browserWindow?.navigator.onLine === false;
			const isHidden = browserDocument?.visibilityState === 'hidden';

			return isOffline || isHidden || Date.now() < resumeGraceUntil;
		},
		dispose: () => {
			browserDocument?.removeEventListener('visibilitychange', handleVisibilityChange);
			browserWindow?.removeEventListener('online', handleVisibilityChange);
		},
	};
};
